/* eslint-disable no-nested-ternary */
import ReactDom from 'react-dom';

import { CustomPicker } from '@components/CustomPickerWithBorder/CustomPicker';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { Icon as BlueIcon } from '@blueprintjs/core';

import { State } from '@hookstate/core';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { indexTagsCategory } from '@services/queries/tagsCategory';
import { verifyInUseTagCategory } from '@services/queries/tagsCategory/verifyInUseTagCategory/verifyInUseTagCategory';
import { ITagCategory } from '@models/TagCategory.model';
import { deleteTagCategory } from '@services/queries/tagsCategory/deleteTagCategory/deleteTagCategory';
import { deleteInUseTagCategory } from '@services/queries/tagsCategory/deleteInUseTagCategory/deleteInUseTagCategory';

import {
  Container,
  Overlay,
  Delete,
  Cancel,
  Buttons,
  Header,
  Text,
  RowDiv,
  CloseButton,
  Bold,
  RedBold,
  Divider,
  Form,
} from './Deletedialog.styles';

interface OwnProps {
  open: State<boolean>;
  tagCategory: ITagCategory;
}

interface IFormData {
  categoryName: string;
}

export const DeleteDialog = ({ open, tagCategory }: OwnProps) => {
  const portalDiv = document.getElementById('portal');

  const { data } = useQuery(['tagsCategory'], () => indexTagsCategory());

  const Options = data?.map(option => {
    return { value: option.id, label: `${option.name}` };
  });

  const queryClient = useQueryClient();

  const schema = yup
    .object({
      categoryName: yup.string().required(),
    })
    .required();

  const {
    handleSubmit,
    formState: { isValid },
    control,
    reset,
  } = useForm<IFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      categoryName: data?.find(category => category.name === 'Uncategorized')
        ?.id,
    },
    mode: 'onChange',
  });

  const close = () => {
    open.set(false);
    reset();
  };

  const deleteTagCategoryMutation = useMutation(deleteTagCategory, {
    onSuccess: () => {
      queryClient.invalidateQueries(['tagCategories']);
      close();
    },
  });

  const deleteInUseTagCategoryMutation = useMutation(deleteInUseTagCategory, {
    onSuccess: () => {
      queryClient.invalidateQueries(['tagCategories']);
      close();
    },
  });

  const onSubmit = (formData: IFormData) => {
    deleteInUseTagCategoryMutation.mutate({
      id: tagCategory.id,
      newTagCategoryId: formData.categoryName,
    });
  };

  const submitNotInUse = () => {
    deleteTagCategoryMutation.mutate(tagCategory.id);
  };

  const verifyStatus = useQuery(['tagsCategory', tagCategory.id, 'inUse'], () =>
    verifyInUseTagCategory(tagCategory.id),
  );

  if (verifyStatus.isLoading) {
    return <div />;
  }

  if (verifyStatus.isError) {
    return <div />;
  }

  if (!verifyStatus.data) {
    return <div />;
  }

  return open.get() && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            {(() => {
              if (verifyStatus.data?.onlyLeft) {
                return (
                  <>
                    <RowDiv
                      onClick={() => {
                        open.set(false);
                      }}
                    >
                      <Header>Tag Category cannot be deleted</Header>
                      <CloseButton>
                        <BlueIcon icon="cross" color="#8F95B2" />
                      </CloseButton>
                    </RowDiv>
                    <Divider height="16px" />
                    <Text>
                      When deleting a tag category you must select what tag
                      category will replace the one being deleted, and there are
                      no other tag category for this Studio.
                    </Text>
                    <Divider height="28px" />
                    <Text>
                      Please add a new tag category before deleting this.
                    </Text>
                    <Divider height="28px" />
                    <Buttons>
                      <Cancel
                        onClick={() => {
                          open.set(false);
                        }}
                      >
                        Cancel
                      </Cancel>
                    </Buttons>
                  </>
                );
              }

              if (!verifyStatus.data?.inUse) {
                return (
                  <>
                    <RowDiv
                      onClick={() => {
                        open.set(false);
                      }}
                    >
                      <Header>Are you sure?</Header>
                      <CloseButton>
                        <BlueIcon icon="cross" color="#8F95B2" />
                      </CloseButton>
                    </RowDiv>
                    <Text>This action cannot be undone.</Text>
                    <Buttons>
                      <Delete onClick={submitNotInUse}>Delete</Delete>
                    </Buttons>
                  </>
                );
              }

              return (
                <>
                  <RowDiv
                    onClick={() => {
                      open.set(false);
                    }}
                  >
                    <Header>Are you sure?</Header>
                    <CloseButton>
                      <BlueIcon icon="cross" color="#8F95B2" />
                    </CloseButton>
                  </RowDiv>
                  <Text>
                    You&apos;re about to permanently delete{' '}
                    <RedBold>a category</RedBold>, being used by{' '}
                    <Bold>{verifyStatus.data?.assets} tags</Bold>.
                  </Text>
                  <Divider height="16px" />
                  <Form onSubmit={handleSubmit(onSubmit)}>
                    <Text className="label">Replacement Category</Text>
                    <Controller
                      control={control}
                      name="categoryName"
                      render={({ field: { onChange, value } }) => (
                        <CustomPicker
                          options={Options}
                          placeholder="Select a Category"
                          value={Options?.find(c => c.value === value)}
                          onChange={(val: any) => {
                            onChange(val.value);
                          }}
                        />
                      )}
                    />

                    <Text className="note">
                      By default all tags will be uncategorized
                    </Text>

                    <Divider height="28px" />

                    <Buttons>
                      <Cancel
                        onClick={() => {
                          open.set(false);
                        }}
                      >
                        Cancel
                      </Cancel>

                      <Delete disabled={!isValid} type="submit">
                        Delete
                      </Delete>
                    </Buttons>
                  </Form>
                </>
              );
            })()}
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
